import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Users, DollarSign, Copy, Check, Gift, 
  TrendingUp, Loader2, ExternalLink 
} from 'lucide-react';


const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ReferralEarningsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/referral-revenue/my-referrals`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (error) {
      console.error('Failed to load referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    try {
      setGenerating(true);
      const res = await fetch(`${API_URL}/api/referral-revenue/generate-code`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        loadReferralData();
      }
    } catch (error) {
      alert('Failed to generate code');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWithdraw = async () => {
    try {
      setWithdrawing(true);
      const res = await fetch(`${API_URL}/api/referral-revenue/withdraw`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const result = await res.json();
        alert(`Successfully withdrew ${result.amount} MTR!`);
        loadReferralData();
      } else {
        const error = await res.json();
        alert(error.detail || 'Withdrawal failed');
      }
    } catch (error) {
      alert('Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      
    );
  }

  // Show generate code screen if no code yet
  if (!data || !data.has_code) {
    return (
      
        <div className="max-w-2xl mx-auto p-6 mt-12">
          <Card>
            <CardContent className="p-12 text-center space-y-6">
              <Users className="w-16 h-16 mx-auto text-blue-500" />
              <div>
                <h2 className="text-2xl font-bold mb-2">Start Earning Referral Income</h2>
                <p className="text-gray-600">
                  Generate your unique referral code and earn lifetime commissions from users you refer!
                </p>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900 p-6 rounded-lg space-y-3 text-left">
                <h3 className="font-semibold">💰 How You Earn:</h3>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>5 MTR</strong> when someone signs up with your code</li>
                  <li>• <strong>10 MTR</strong> when they make their first deposit</li>
                  <li>• <strong>5%</strong> of all their transactions (lifetime)</li>
                  <li>• <strong>2%</strong> of all their deposits (lifetime)</li>
                  <li>• <strong>10%</strong> of all their purchases (lifetime)</li>
                </ul>
              </div>

              <Button
                onClick={generateCode}
                disabled={generating}
                size="lg"
                className="w-full"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Gift className="w-4 h-4 mr-2" />
                    Generate My Referral Code
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      
    );
  }

  return (
    
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="w-8 h-8 text-blue-500" />
            Referral Earnings
          </h1>
          <p className="text-gray-600 mt-1">
            Earn lifetime commissions by referring users to MTR
          </p>
        </div>

        {/* Referral Link Card */}
        <Card>
          <CardHeader>
            <CardTitle>Your Referral Link</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Referral Code</label>
              <div className="flex gap-2">
                <Input
                  value={data.code}
                  readOnly
                  className="font-mono text-lg"
                />
                <Button
                  onClick={() => copyToClipboard(data.code)}
                  variant="outline"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Share Link</label>
              <div className="flex gap-2">
                <Input
                  value={data.share_link}
                  readOnly
                  className="text-sm"
                />
                <Button
                  onClick={() => copyToClipboard(data.share_link)}
                  variant="outline"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Share this link with friends and start earning commissions from their activity
            </p>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Signups</p>
                  <p className="text-2xl font-bold">{data.stats.total_signups}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Earned</p>
                  <p className="text-2xl font-bold">{data.stats.total_earned} MTR</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{data.stats.pending_payout} MTR</p>
                </div>
                <DollarSign className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Paid Out</p>
                  <p className="text-2xl font-bold text-blue-600">{data.stats.paid_out} MTR</p>
                </div>
                <ExternalLink className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Withdraw Button */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Ready to withdraw?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Minimum payout: {data.stats.minimum_payout} MTR
                  {!data.stats.can_withdraw && ` (${data.stats.pending_payout} / ${data.stats.minimum_payout})`}
                </p>
              </div>
              <Button
                onClick={handleWithdraw}
                disabled={!data.stats.can_withdraw || withdrawing}
                size="lg"
              >
                {withdrawing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Withdraw {data.stats.pending_payout} MTR
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Referred Users */}
        <Card>
          <CardHeader>
            <CardTitle>Your Referrals ({data.referred_users?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {data.referred_users && data.referred_users.length > 0 ? (
              <div className="space-y-2">
                {data.referred_users.map((user) => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{user.display_name || user.username}</p>
                      <p className="text-xs text-gray-500">@{user.username}</p>
                    </div>
                    <Badge variant="secondary">
                      {new Date(user.referred_at).toLocaleDateString()}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                No referrals yet. Start sharing your link!
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Earnings */}
        {data.recent_earnings && data.recent_earnings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Earnings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.recent_earnings.map((earning) => (
                  <div 
                    key={earning.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium">{earning.reason}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(earning.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant="secondary">
                      +{earning.amount} MTR
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Commission Rates */}
        <Card>
          <CardHeader>
            <CardTitle>💰 Your Commission Rates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm">Transaction Commission</span>
              <Badge>{data.config?.transaction_commission}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Deposit Commission</span>
              <Badge>{data.config?.deposit_commission}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Purchase Commission</span>
              <Badge>{data.config?.purchase_commission}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm">Lifetime Commissions</span>
              <Badge variant={data.config?.lifetime_commissions ? "default" : "secondary"}>
                {data.config?.lifetime_commissions ? "Enabled ✓" : "Limited Time"}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    
  );
}
